import type { ReactNode } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, LabelList,
} from 'recharts';
import dayjs from 'dayjs';
import {
  TrendingUp, Wallet, Boxes, CalendarCheck, Route, MessageSquareWarning, Store, Receipt, BarChart3,
  Banknote, Smartphone, Landmark, ScrollText, CreditCard, CircleDashed, Monitor, UserCog, Bot,
  ArrowDownWideNarrow, Filter, IndianRupee, Hash, Percent, Clock, Sigma, Rows3,
} from 'lucide-react';
import { cn, StatusBadge, Select, Input, InputNumber, Tag } from '../../ui';
import { fileUrl } from '../../api/client';
import type { TagTone } from '../../ui';
import { roleTone } from '../../components/RoleBadge';
import { inr, inr2 } from '../../lib/format';
import { PALETTE } from '../../lib/palette';
import { useThemeStore } from '../../store/theme';

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Types mirrored from the report catalog
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

export type ReportColumnType =
  | 'text' | 'code' | 'money' | 'int' | 'decimal' | 'percent'
  | 'date' | 'datetime' | 'status' | 'days' | 'hours' | 'list';

export interface ReportColumn {
  key: string; header: string; type?: ReportColumnType; total?: boolean; avg?: boolean;
  /**
   * Which way this column's money goes — 'out' (owed / leaving) or 'in' (received / headroom).
   * Only these columns are coloured; every other amount is plain, so a colour still means something.
   */
  tone?: 'in' | 'out' | 'plain';
  /** A deadline date (an expiry): red once it is behind today. Declared — most report dates are past. */
  lapses?: boolean;
  /** Carried by the report but off the default view — one click away in the column picker. */
  secondary?: boolean;
}
export type ReportOptionBadgeKind = 'role' | 'status' | 'level' | 'severity' | 'plain';

export interface ReportFilterOption {
  value: string;
  label: string;
  /** The attribute worth colouring — a territory's level, a staff role, a retailer's status. */
  badge?: string;
  badgeKind?: ReportOptionBadgeKind;
  /** The quiet disambiguator: a parent territory, a retailer code, a masked account number. */
  hint?: string;
  /** A thumbnail — a pack shot is recognised faster than "AGN 0.5L" is read. */
  image?: string;
  /** Name of a pictogram from {@link OPTION_ICONS}; an unknown name simply draws nothing. */
  icon?: string;
  /** The value of the filter this option's filter `dependsOn`. */
  parent?: string;
  /**
   * Like `parent`, for an option belonging to SEVERAL parents — a sales person covers more than one
   * territory. Already expanded up the territory tree by the server, so a district assignment also
   * matches its state and zone.
   */
  parents?: string[];
}

export interface ReportFilterDef {
  key: string; label: string;
  type: 'select' | 'multiselect' | 'text' | 'number';
  options?: ReportFilterOption[];
  /** Every option is a code of this kind — draw each as the pill it is elsewhere in the app. */
  optionKind?: ReportOptionBadgeKind;
  /** Options narrow to those whose `parent` matches the named filter's current value. */
  dependsOn?: string;
  default?: string | number;
  placeholder?: string;
  hint?: string;
}
export interface ReportChartDef {
  kind: 'line' | 'bar' | 'donut';
  x: string | string[];
  y: string;
  reverse?: boolean;
  /** Column whose value gives each bar its colour — the product family, the role, the severity. */
  colorBy?: string;
}
export interface ReportDefinition {
  code: string; name: string; category: string; description: string;
  columns: ReportColumn[];
  filters?: ReportFilterDef[];
  chart?: ReportChartDef;
  pointInTime?: boolean;
  /** One line saying which date the period actually filters. */
  periodMeans?: string;
  /** The period runs forward from today (an expiry window). */
  periodAhead?: boolean;
  rowLimit?: number | null;
  scope?: string;
}
export interface ReportSummaryTile {
  key: string; label: string; value: number; type: ReportColumnType; kind: 'count' | 'sum' | 'avg';
  /**
   * This tile IS a slice of the report — the filter and value that reproduce it.
   *
   * Declared by the report's own column (see `ReportColumn.drill` in the catalog), so the tile and
   * the filter cannot drift into meaning different things.
   */
  drill?: { filter: string; value: string | string[] };
}

export const NUMERIC_TYPES: ReadonlySet<ReportColumnType> = new Set<ReportColumnType>(
  ['money', 'int', 'decimal', 'percent', 'days', 'hours'],
);

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Category identity
 *
 * Each of the eight categories gets an icon and a hue, used identically on its shelf heading, its
 * cards, the rail and the open report's header — so a reader who has learnt that gold means stock
 * can find the stock reports without reading a word. The eight are navigation chrome, not a data
 * encoding, which is why two of them may share a hue family at different weights; the icon and the
 * label carry the identity, colour only speeds the search up.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

export type Tone = 'brand' | 'sky' | 'harvest' | 'plum' | 'earth' | 'danger' | 'success' | 'teal' | 'indigo' | 'slate';

export const TONE: Record<Tone, { chip: string; wash: string; rail: string; railBorder: string; ink: string; dot: string }> = {
  brand: {
    chip: 'bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300',
    wash: 'border-brand-500/25 bg-brand-500/[0.05] dark:bg-brand-500/[0.08]',
    rail: 'bg-brand-500', railBorder: 'border-l-brand-500', ink: 'text-brand-700 dark:text-brand-300', dot: 'bg-brand-500',
  },
  sky: {
    chip: 'bg-sky-500/12 text-sky-600 dark:bg-sky-500/20 dark:text-sky-400',
    wash: 'border-sky-500/25 bg-sky-500/[0.05] dark:bg-sky-500/[0.08]',
    rail: 'bg-sky-500', railBorder: 'border-l-sky-500', ink: 'text-sky-600 dark:text-sky-400', dot: 'bg-sky-500',
  },
  harvest: {
    chip: 'bg-harvest-100 text-harvest-700 dark:bg-harvest-400/20 dark:text-harvest-300',
    wash: 'border-harvest-400/35 bg-harvest-400/[0.08] dark:bg-harvest-400/[0.10]',
    rail: 'bg-harvest-400', railBorder: 'border-l-harvest-400', ink: 'text-harvest-700 dark:text-harvest-300', dot: 'bg-harvest-400',
  },
  plum: {
    chip: 'bg-plum-400/15 text-plum-600 dark:bg-plum-400/20 dark:text-plum-400',
    wash: 'border-plum-400/25 bg-plum-400/[0.06] dark:bg-plum-400/[0.09]',
    rail: 'bg-plum-500', railBorder: 'border-l-plum-500', ink: 'text-plum-600 dark:text-plum-400', dot: 'bg-plum-500',
  },
  earth: {
    chip: 'bg-earth-400/15 text-earth-600 dark:bg-earth-400/20 dark:text-earth-400',
    wash: 'border-earth-400/25 bg-earth-400/[0.06] dark:bg-earth-400/[0.09]',
    rail: 'bg-earth-500', railBorder: 'border-l-earth-500', ink: 'text-earth-600 dark:text-earth-400', dot: 'bg-earth-500',
  },
  danger: {
    chip: 'bg-danger-500/12 text-danger-600 dark:bg-danger-500/20 dark:text-danger-500',
    wash: 'border-danger-500/25 bg-danger-500/[0.05] dark:bg-danger-500/[0.08]',
    rail: 'bg-danger-500', railBorder: 'border-l-danger-500', ink: 'text-danger-600 dark:text-danger-500', dot: 'bg-danger-500',
  },
  success: {
    chip: 'bg-success-500/15 text-success-600 dark:bg-success-500/20 dark:text-success-500',
    wash: 'border-success-500/25 bg-success-500/[0.05] dark:bg-success-500/[0.08]',
    rail: 'bg-success-500', railBorder: 'border-l-success-500', ink: 'text-success-600 dark:text-success-500', dot: 'bg-success-500',
  },
  teal: {
    chip: 'bg-teal-500/12 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300',
    wash: 'border-teal-500/25 bg-teal-500/[0.05] dark:bg-teal-500/[0.08]',
    rail: 'bg-teal-500', railBorder: 'border-l-teal-500', ink: 'text-teal-700 dark:text-teal-300', dot: 'bg-teal-500',
  },
  indigo: {
    chip: 'bg-indigo-500/12 text-indigo-600 dark:bg-indigo-400/20 dark:text-indigo-300',
    wash: 'border-indigo-500/25 bg-indigo-500/[0.05] dark:bg-indigo-400/[0.08]',
    rail: 'bg-indigo-500', railBorder: 'border-l-indigo-500', ink: 'text-indigo-600 dark:text-indigo-300', dot: 'bg-indigo-500',
  },
  slate: {
    chip: 'bg-surface-3 text-muted', wash: 'border-line bg-surface-2',
    rail: 'bg-line-strong', railBorder: 'border-l-line-strong', ink: 'text-muted', dot: 'bg-subtle',
  },
};

interface CategoryMeta { tone: Tone; icon: typeof TrendingUp; blurb: string }

export const CATEGORY: Record<string, CategoryMeta> = {
  Sales: { tone: 'brand', icon: TrendingUp, blurb: 'What was ordered, by whom and through which channel' },
  Collection: { tone: 'sky', icon: Wallet, blurb: 'Money received, money owed and how old the debt is' },
  Inventory: { tone: 'harvest', icon: Boxes, blurb: 'Stock on hand, its value, what expires and what to reorder' },
  Attendance: { tone: 'plum', icon: CalendarCheck, blurb: 'Field days marked, visits made and days that were flagged' },
  Route: { tone: 'earth', icon: Route, blurb: 'Route plans and the ground they cover' },
  Complaint: { tone: 'danger', icon: MessageSquareWarning, blurb: 'Open complaints, their age and the SLA clock' },
  // Retailer was `success` — a lime one shade off Sales' brand green, so the two shelves read as one
  // area — and GST was `slate`, which is no colour at all. Each now has a hue nobody else uses.
  Retailer: { tone: 'teal', icon: Store, blurb: 'Accounts opened, and accounts that have gone quiet' },
  GST: { tone: 'indigo', icon: Receipt, blurb: 'Tax position and the state of invoice verification' },
};

/** A category the catalog invents later still gets an icon and a hue rather than nothing. */
export const categoryMeta = (name: string): CategoryMeta =>
  CATEGORY[name] ?? { tone: 'slate', icon: BarChart3, blurb: '' };

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Typed cells
 *
 * The catalog declares what each column HOLDS, so the table can right-align a figure, print rupees
 * as rupees and a date as `07 Aug 2026` — instead of the raw `146250.00` and
 * `2026-08-07T18:30:00.000Z` every report column used to show. The same declarations drive the
 * workbook and the PDF server-side, so all three agree.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

const num = (v: any, dp = 0) =>
  Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp });

/** JSON array columns (`anomaly_flags`) read as chips, not as `["AUTO_CHECKOUT"]`. */
const asList = (v: any): string[] => {
  if (Array.isArray(v)) return v.map(String);
  try { const p = JSON.parse(String(v)); return Array.isArray(p) ? p.map(String) : [String(v)]; } catch { return [String(v)]; }
};

/** Is the filter this one depends on actually set? */
export function hasParentValue(filter: ReportFilterDef, values?: Record<string, any>): boolean {
  const v = filter.dependsOn ? values?.[filter.dependsOn] : undefined;
  if (v === undefined || v === null || v === '') return false;
  return !(Array.isArray(v) && v.length === 0);
}

/**
 * The options a dependent filter can still offer, given what its parent is set to.
 *
 * Exported because two places need the SAME answer: the control, which must not offer an option that
 * can only return no rows, and the page, which must notice when a value CHOSEN EARLIER has just been
 * contradicted by a parent picked after it — a retailer in Maharashtra left sitting under a Delhi
 * territory is a report that reads as though it was scoped to both and was really scoped to neither.
 */
export function visibleOptions(filter: ReportFilterDef, values?: Record<string, any>): ReportFilterOption[] {
  if (!hasParentValue(filter, values)) return filter.options ?? [];
  const raw = values?.[filter.dependsOn!];
  const parents = new Set((Array.isArray(raw) ? raw : [raw]).map(String));
  return (filter.options ?? []).filter((o) => {
    // An option that declares no parentage is never hidden — only options that DO claim a parent can
    // be contradicted by the one that is selected.
    if (o.parents?.length) return o.parents.some((x) => parents.has(String(x)));
    return o.parent == null || parents.has(String(o.parent));
  });
}

export function ReportCell({ value, type = 'text', tone, lapses }: {
  value: any; type?: ReportColumnType; tone?: 'in' | 'out' | 'plain'; lapses?: boolean;
}) {
  if (value === null || value === undefined || value === '') return <span className="text-subtle">—</span>;
  /*
   * Nothing happened here — a payment row has no debit, a rep with no route has no distance — so the
   * cell says nothing instead of printing ₹0.00 / 0 / 0% for the reader to dismiss. Every numeric
   * column, and the same rule in the PDF and the workbook.
   */
  if (NUMERIC_TYPES.has(type)) {
    const z = Number(value);
    if (Number.isFinite(z) && Math.abs(z) < 0.005) return <span className="text-subtle">—</span>;
  }
  switch (type) {
    case 'money': {
      const n = Number(value);
      // Ink only where the column declared a direction: 'out' is red while it is owed and green when
      // it has swung into the account's favour; 'in' is the other way round. Everything else is plain.
      // 'plain' is a running position — owed on nearly every row, so colouring it would paint the
      // whole column one colour and say nothing; its sign and the Dr/Cr beside it carry the direction.
      const ink = tone === 'plain' ? ''
        : tone === 'out' ? (n > 0 ? 'text-danger-600' : 'text-success-600')
          : tone === 'in' ? (n > 0 ? 'text-success-600' : 'text-danger-600')
            : n < 0 ? 'text-danger-600' : '';
      return <span className={cn('tabular-nums', ink)}>{inr2(value)}</span>;
    }
    case 'int': return <span className="tabular-nums">{num(value)}</span>;
    case 'decimal': return <span className="tabular-nums">{num(value, Number.isInteger(Number(value)) ? 0 : 2)}</span>;
    case 'percent': return <span className="tabular-nums">{num(value, 1)}%</span>;
    // A negative count of days is a deadline already missed — "-12 d" left on an expiry. Red, as the PDF prints it.
    case 'days': return <span className={cn('tabular-nums', Number(value) < 0 && 'text-danger-600')}>{num(value)} d</span>;
    case 'hours': return <span className={cn('tabular-nums', Number(value) < 0 && 'text-danger-600')}>{num(value)} h</span>;
    case 'date': {
      const d = dayjs(value);
      const lapsed = lapses && d.isValid() && d.isBefore(dayjs(), 'day');
      return <span className={cn('tabular-nums', lapsed && 'font-medium text-danger-600')}>{d.isValid() ? d.format('DD MMM YYYY') : String(value)}</span>;
    }
    case 'datetime': return <span className="tabular-nums">{dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY, HH:mm') : String(value)}</span>;
    case 'status': return <StatusBadge status={String(value)} />;
    case 'code': return <span className="font-medium tabular-nums text-content">{String(value)}</span>;
    case 'list': return (
      <span className="flex flex-wrap gap-1">
        {asList(value).map((f) => (
          <span key={f} className="rounded-pill bg-warning-500/15 px-1.5 py-px type-label text-warning-600 dark:text-harvest-300">
            {f.replace(/_/g, ' ')}
          </span>
        ))}
      </span>
    );
    default: return <span>{String(value)}</span>;
  }
}

/** The plain-text form of a cell — for the client-side search box, which cannot search a ReactNode. */
export const cellText = (value: any, type: ReportColumnType = 'text'): string => {
  if (value === null || value === undefined) return '';
  if (type === 'date' || type === 'datetime') return dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY') : String(value);
  if (type === 'list') return asList(value).join(' ');
  return String(value);
};

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Summary band
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/** ₹1.55 Cr / ₹6.52 L — a headline figure, where the exact rupee is noise. */
const inrShort = (v: any): string => {
  const n = Number(v ?? 0);
  const a = Math.abs(n);
  if (a >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (a >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)} L`;
  return inr(n);
};

const tileValue = (t: ReportSummaryTile): string => {
  switch (t.type) {
    case 'money': return t.kind === 'avg' ? inr(t.value) : inrShort(t.value);
    case 'percent': return `${num(t.value, 1)}%`;
    case 'days': return `${num(t.value)} d`;
    case 'hours': return `${num(t.value)} h`;
    case 'int': return num(Math.round(t.value));
    default: return num(t.value, Number.isInteger(t.value) ? 0 : 2);
  }
};

/**
 * The tile's hue says what KIND of figure it is, not which position it happens to occupy.
 *
 * Colouring by index would repaint every tile the moment a filter drops a column from the summary —
 * money would turn blue because a count vanished — so the reader could never learn what a colour
 * means. Keyed on the measure instead: rupees are always the brand green, counts blue, an average
 * violet, an age gold. The report's own category tone opens the row on the row-count tile, which is
 * the one figure every report has.
 */
const TILE_TONE = (tile: ReportSummaryTile, reportTone: Tone): Tone => {
  if (tile.key === '__rows') return reportTone;
  if (tile.kind === 'avg') return 'plum';
  switch (tile.type) {
    case 'money': return 'brand';
    case 'int': return 'sky';
    case 'percent': return 'plum';
    case 'days': case 'hours': return 'harvest';
    default: return 'success';
  }
};

/** The glyph for what KIND of figure a tile is — it follows the same key as its hue, so the two agree. */
const TILE_ICON = (tile: ReportSummaryTile): typeof Hash => {
  if (tile.key === '__rows') return Rows3;
  if (tile.kind === 'avg') return Sigma;
  switch (tile.type) {
    case 'money': return IndianRupee;
    case 'percent': return Percent;
    case 'days': case 'hours': return Clock;
    default: return Hash;
  }
};

/**
 * The strip tile's resting and lit backgrounds, per hue. A top-down fade rather than a flat fill, so
 * the colour sits under the label and the figure stays on a near-plain ground.
 *
 * Literal strings, one per tone: Tailwind only emits a class it can find written out in the source.
 * Resting and lit are SEPARATE sets because `cn()` has no tailwind-merge — two background classes on
 * one element would be settled by stylesheet order, not by which was passed last.
 */
const STRIP_WASH: Record<Tone, { rest: string; lit: string }> = {
  brand: { rest: 'bg-linear-to-b from-brand-500/[0.12] to-brand-500/[0.02]', lit: 'bg-brand-500/[0.18]' },
  sky: { rest: 'bg-linear-to-b from-sky-500/[0.12] to-sky-500/[0.02]', lit: 'bg-sky-500/[0.18]' },
  harvest: { rest: 'bg-linear-to-b from-harvest-400/[0.18] to-harvest-400/[0.03]', lit: 'bg-harvest-400/[0.24]' },
  plum: { rest: 'bg-linear-to-b from-plum-500/[0.11] to-plum-500/[0.02]', lit: 'bg-plum-500/[0.17]' },
  earth: { rest: 'bg-linear-to-b from-earth-500/[0.11] to-earth-500/[0.02]', lit: 'bg-earth-500/[0.17]' },
  danger: { rest: 'bg-linear-to-b from-danger-500/[0.10] to-danger-500/[0.02]', lit: 'bg-danger-500/[0.16]' },
  success: { rest: 'bg-linear-to-b from-success-500/[0.12] to-success-500/[0.02]', lit: 'bg-success-500/[0.18]' },
  teal: { rest: 'bg-linear-to-b from-teal-500/[0.12] to-teal-500/[0.02]', lit: 'bg-teal-500/[0.18]' },
  indigo: { rest: 'bg-linear-to-b from-indigo-500/[0.11] to-indigo-500/[0.02]', lit: 'bg-indigo-500/[0.17]' },
  slate: { rest: 'bg-surface-2', lit: 'bg-surface-3' },
};

/**
 * The figures above the table.
 *
 * These are a summary OF THE ROWS SHOWN — not a separate whole-period aggregate — so when the report
 * was capped the caption says so rather than letting a partial total read as the company's number.
 * Only the columns the report itself marked as worth totalling appear.
 */
/** Is the filter currently set to exactly what this tile's drill asks for? Order-insensitive. */
function drillIsOn(current: any, want: string | string[]): boolean {
  const norm = (v: any) => (v === undefined || v === null || v === '' ? [] : (Array.isArray(v) ? v : [v]).map(String).sort());
  const a = norm(current); const b = norm(want);
  return a.length > 0 && a.length === b.length && a.every((x, i) => x === b[i]);
}

/**
 * The figures above the table — and the way into the rows behind each of them.
 *
 * A tile does ONE of two things, decided by the report itself rather than by this component:
 *
 *   • **Opens its slice.** A column that is `SUM(CASE WHEN nature = 'INTER_STATE' …)` declares the
 *     filter it corresponds to (`ReportColumn.drill`), so pressing "Inter-state ₹6,165" re-runs the
 *     report filtered to inter-state. The filter is the report's own — the same one in the bar above,
 *     URL-backed, so the drill is shareable and Back undoes it — and pressing the lit tile clears it.
 *   • **Orders the table by its measure.** For a figure that is not a slice (taxable value, tax),
 *     "why is that number so big?" is answered by putting the biggest rows on top.
 *
 * A tile reading ZERO is inert. Filtering to nothing is a dead end, and the standing rule is that a
 * tile you cannot open should not look like one.
 */
export function SummaryBand({ tiles, tone, truncated, sortKey, onSort, filters, onDrill, layout = 'grid' }: {
  tiles: ReportSummaryTile[];
  tone: Tone;
  truncated: boolean;
  /**
   * `'grid'` is the original block of cards. `'strip'` is the reporting workspace's version: the same
   * figures, the same drill and sort, on ONE 56px band instead of four-to-eight cards that wrapped to
   * two rows and pushed the table off the screen. Nothing about what a tile MEANS changes with it.
   */
  layout?: 'grid' | 'strip';
  /** The measure the table is currently ordered by, so the tile can show it. */
  sortKey?: string | null;
  /**
   * Order the table by a tile's measure. Every headline figure is a question — "why is discount that
   * high?" — and the answer is always in the rows underneath, so the tile is the way into them.
   * Omit to render a plain, non-interactive band.
   */
  onSort?: (key: string | null) => void;
  /** The report's live filter values, to light the tile whose slice is already open. */
  filters?: Record<string, any>;
  /** Apply (or clear, with `undefined`) one of the report's own filters. */
  onDrill?: (key: string, value: string | string[] | undefined) => void;
}) {
  if (!tiles.length) return null;
  const strip = layout === 'strip';
  return (
    <div className={cn(
      strip
        // One band, scrolled sideways rather than wrapped: a summary that grows a row costs the table
        // a row, and the figures are read left-to-right anyway.
        ? 'flex items-stretch divide-x divide-line overflow-x-auto border-b border-line bg-surface'
        : 'grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-4',
    )}>
      {tiles.map((tile) => {
        const t = TONE[TILE_TONE(tile, tone)];
        // The row-count tile counts rows rather than measuring a column, so it has nothing to sort
        // by; clicking it puts the table back in the order the report returned.
        const target = tile.key === '__rows' ? null : tile.key.replace(/__(max|distinct)$/, '');
        const label = tile.kind === 'avg' && !/^avg/i.test(tile.label) ? `Avg ${tile.label.toLowerCase()}` : tile.label;

        /*
         * A slice tile filters; everything else sorts.
         *
         * A slice reading ZERO is inert — and deliberately does NOT fall back to sorting: there is
         * nothing to open and nothing to put on top, so the tile renders as a plain div rather than
         * a button that leads nowhere (the standing rule about tiles you cannot open).
         */
        const isSlice = !!(onDrill && tile.drill);
        const canDrill = isSlice && tile.value !== 0;
        const drillOn = !!(tile.drill && drillIsOn(filters?.[tile.drill.filter], tile.drill.value));
        const sortOn = !isSlice && !!onSort && sortKey != null && target === sortKey;
        const active = isSlice ? drillOn : sortOn;
        const interactive = isSlice ? canDrill : !!onSort;
        const Tag = interactive ? 'button' : 'div';

        const action = isSlice
          ? (canDrill
            ? { onClick: () => onDrill!(tile.drill!.filter, drillOn ? undefined : tile.drill!.value),
                title: drillOn ? 'Showing only this — click to clear' : `Show only ${label.toLowerCase()}` }
            : null)
          : onSort
            ? { onClick: () => onSort(sortOn ? null : target),
                title: target
                  ? (sortOn ? 'Sorted by this — click to restore the report order' : `Sort the table by ${label.toLowerCase()}`)
                  : 'Restore the report order' }
            : null;

        const mark = (
          <>
            {/* Two different marks, because they mean two different things — a funnel says the list
                below is now only this; the sort arrow says it is merely re-ordered. */}
            {drillOn && <Filter className="size-3 shrink-0" aria-hidden />}
            {sortOn && <ArrowDownWideNarrow className="size-3 shrink-0" aria-hidden />}
            {canDrill && !drillOn && <Filter className="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" aria-hidden />}
          </>
        );

        if (strip) {
          const tileTone = TILE_TONE(tile, tone);
          const Icon = TILE_ICON(tile);
          return (
            <Tag
              key={tile.key}
              {...(action ? { type: 'button' as const, 'aria-pressed': active, ...action } : {})}
              className={cn(
                // Each figure wears the hue of what it measures — rupees green, counts blue, averages
                // violet, ages gold — as a top rail and a fading wash. The FIGURE stays in plain ink:
                // colour on a money value means "which way it went" in this app, and a total has no way.
                // `grow basis-0`: the tiles share the whole band instead of stopping part-way and leaving
                // a white stretch to the right edge.
                'group relative min-w-[124px] shrink-0 grow basis-0 overflow-hidden px-3 pt-2 pb-1.5 text-left',
                active ? STRIP_WASH[tileTone].lit : STRIP_WASH[tileTone].rest,
                interactive && 'transition-[filter] hover:brightness-[0.97] dark:hover:brightness-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/50',
              )}
            >
              <span className={cn('absolute inset-x-0 top-0 h-0.75', t.rail)} aria-hidden />
              {/* The active mark is an underline rather than a ring: on a band of eight figures a ring per
                  tile reads as eight boxes, which is the card grid this replaced. */}
              {active && <span className={cn('absolute inset-x-0 bottom-0 h-0.5', t.rail)} aria-hidden />}
              <span className={cn('flex items-center gap-1 truncate type-label font-medium', t.ink)}>
                <Icon className="size-3 shrink-0 opacity-80" aria-hidden />
                <span className="truncate">
                  {label}
                  {tile.key === '__rows' && truncated ? ' (capped)' : ''}
                </span>
                {mark}
              </span>
              <span className="mt-px block truncate text-[15px] font-semibold leading-5 tabular-nums text-content" title={tileValue(tile)}>
                {tileValue(tile)}
              </span>
            </Tag>
          );
        }

        return (
          <Tag
            key={tile.key}
            {...(action ? { type: 'button' as const, 'aria-pressed': active, ...action } : {})}
            className={cn(
              'group relative min-w-0 overflow-hidden rounded-card border px-3.5 py-3 text-left',
              t.wash,
              interactive && 'transition-shadow hover:shadow-pop focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
              active && 'ring-2 ring-inset ring-brand-500/45',
            )}
          >
            <span className={cn('absolute inset-y-0 left-0', active ? 'w-1.5' : 'w-1', t.rail)} aria-hidden />
            <p className={cn('flex items-center gap-1 truncate type-overline', t.ink)}>
              <span className="truncate">
                {label}
                {tile.key === '__rows' && truncated ? ' (capped)' : ''}
              </span>
              {mark}
            </p>
            <p className="mt-1 truncate type-metric-sm tabular-nums text-content" title={tileValue(tile)}>
              {tileValue(tile)}
            </p>
          </Tag>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * The picture
 *
 * The catalog nominates the measure worth drawing and the shape to draw it in — a report knows which
 * of its columns is the point, and guessing from column types picks the wrong one the moment a report
 * has two numeric columns. Reports that are registers rather than measurements (an invoice list, an
 * anomaly queue) declare no chart and get none, instead of a bar chart of nothing in particular.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Categorical hues for the share-of-whole charts, in FIXED order — never cycled.
 *
 * Both sets are drawn from the app's own palette and were checked with a contrast/CVD validator
 * against the light and the dark canvas: adjacent pairs stay apart under protanopia, deuteranopia and
 * tritanopia, every hue clears the chroma floor, and each sits inside its mode's lightness band. Dark
 * mode is a SELECTED set of lighter steps, not an automatic flip of the light one — the light set's
 * plum and red both fall under 3:1 on a dark canvas.
 *
 * Beyond five slices the rest fold into a neutral "Other", which is what keeps a donut readable — a
 * ninth generated hue would be indistinguishable from the eighth anyway.
 */
const SERIES_LIGHT = [PALETTE.brand[700], PALETTE.sky[600], PALETTE.harvest[600], PALETTE.plum[500], PALETTE.danger[600]];
const SERIES_DARK = [PALETTE.brand[500], PALETTE.sky[500], PALETTE.harvest[600], PALETTE.plum[400], PALETTE.danger[500]];
const DONUT_SLOTS = 5;

/**
 * Assign a hue per DISTINCT VALUE of the grouping column, in a fixed order.
 *
 * Keyed on the value, never on the bar's position: every AGN bar is the same green wherever it lands
 * in the ranking, which is what makes the colour mean "AGN" rather than "third biggest". Sorted so
 * the assignment is deterministic — the same data always produces the same picture. Past the five
 * validated slots everything folds into one neutral "Other", because a sixth invented hue would be
 * indistinguishable from the fifth and would only look like a distinction.
 */
const UNGROUPED = '—';

function groupColours(values: (string | null | undefined)[], series: readonly string[], dark: boolean) {
  const NEUTRAL = dark ? '#5b655c' : '#98a29a';
  const norm = (v: any) => (v == null || v === '' ? UNGROUPED : String(v));
  /*
   * A row with no group is not a category — it is the absence of one, so it takes the neutral grey
   * and never consumes a palette slot. Letting it in produced a chart where the loudest hue belonged
   * to "—", which reads as the most important group on the page rather than the least.
   */
  const named = [...new Set(values.map(norm))].filter((v) => v !== UNGROUPED).sort();
  const map = new Map<string, string>();
  named.forEach((v, i) => { if (i < series.length) map.set(v, series[i]); });
  const overflow = Math.max(0, named.length - series.length);
  const hasUngrouped = values.some((v) => norm(v) === UNGROUPED);

  return {
    of: (v: any) => map.get(norm(v)) ?? NEUTRAL,
    legend: [
      ...named.slice(0, series.length).map((v, i) => ({ label: v, colour: series[i] })),
      ...(overflow ? [{ label: `+${overflow} more`, colour: NEUTRAL }] : []),
      ...(hasUngrouped ? [{ label: 'Ungrouped', colour: NEUTRAL }] : []),
    ],
    multi: named.length + (hasUngrouped ? 1 : 0) > 1,
  };
}

const compact = (v: any) => {
  const n = Number(v ?? 0);
  const a = Math.abs(n);
  if (a >= 1_00_00_000) return `${(n / 1_00_00_000).toFixed(1)}Cr`;
  if (a >= 1_00_000) return `${(n / 1_00_000).toFixed(1)}L`;
  if (a >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(Math.round(n));
};

function ChartTooltip({ active, payload, label, valueType, nameLabel }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-control border border-line bg-surface px-2.5 py-1.5 shadow-pop">
      <p className="type-label text-muted">{p.payload?.__label ?? label}</p>
      <p className="type-body-strong tabular-nums text-content">
        {nameLabel}: {valueType === 'money' ? inr2(p.value) : num(p.value, Number.isInteger(Number(p.value)) ? 0 : 2)}
      </p>
    </div>
  );
}

export function ReportVisual({ chart, rows, columns, height = 260 }: {
  chart: ReportChartDef; rows: any[]; columns: ReportColumn[]; height?: number;
}) {
  const dark = useThemeStore((s) => s.resolved)() === 'dark';
  const series = dark ? SERIES_DARK : SERIES_LIGHT;
  const yCol = columns.find((c) => c.key === chart.y);
  // The label may be built from more than one column — a Stock Balance row is a warehouse AND a SKU,
  // and naming it by either alone drew eight bars all reading "AGN".
  const xKeys = Array.isArray(chart.x) ? chart.x : [chart.x];
  const xCol = columns.find((c) => c.key === xKeys[0]);
  const yType = yCol?.type ?? 'decimal';
  const yLabel = yCol?.header ?? chart.y;
  const grid = dark ? '#2e362f' : '#e1e7dd';
  const axisInk = dark ? '#9ba49c' : '#5d6a61';

  /*
   * A composite label drops the parts that are the SAME on every row.
   *
   * Filtering Stock Balance to one warehouse made every bar read "WH-PUNE · PHASEA-17…" — the
   * constant prefix ate the width and the part that actually differed was the part that got cut. A
   * label has to spend its characters on what varies.
   */
  const varying = xKeys.filter((k) => new Set(rows.map((r) => String(r[k] ?? ''))).size > 1);
  const labelKeys = varying.length ? varying : xKeys.slice(-1);

  const labelOf = (r: any) => {
    if (labelKeys.length === 1 && xCol?.type === 'date' && dayjs(r[labelKeys[0]]).isValid()) {
      return dayjs(r[labelKeys[0]]).format('DD MMM');
    }
    const parts = labelKeys.map((k) => r[k]).filter((v) => v !== null && v !== undefined && v !== '');
    return parts.length ? parts.map(String).join(' · ') : '—';
  };

  const data = rows
    .map((r) => ({ __label: labelOf(r), __value: Number(r[chart.y]) || 0, __group: chart.colorBy ? r[chart.colorBy] : undefined }))
    .filter((d) => Number.isFinite(d.__value));
  if (!data.length) return null;
  const ordered = chart.reverse ? [...data].reverse() : data;

  if (chart.kind === 'line') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={ordered} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gbReportFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={series[0]} stopOpacity={0.24} />
              <stop offset="95%" stopColor={series[0]} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={grid} />
          <XAxis dataKey="__label" tickLine={false} axisLine={false} minTickGap={26} tick={{ fontSize: 11, fill: axisInk }} />
          <YAxis tickFormatter={compact} tickLine={false} axisLine={false} width={44} tick={{ fontSize: 11, fill: axisInk }} />
          <RTooltip cursor={{ stroke: grid }} content={<ChartTooltip valueType={yType} nameLabel={yLabel} />} />
          <Area
            type="monotone" dataKey="__value" stroke={series[0]} strokeWidth={2}
            fill="url(#gbReportFill)" dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--gb-surface)' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (chart.kind === 'donut') {
    // Five named slices plus a neutral "Other" — a legend with the figures beside it does the
    // identifying, so nothing here depends on telling two hues apart.
    const sorted = [...ordered].sort((a, b) => b.__value - a.__value);
    const head = sorted.slice(0, DONUT_SLOTS);
    const tail = sorted.slice(DONUT_SLOTS);
    const slices = tail.length
      ? [...head, { __label: `Other (${tail.length})`, __value: tail.reduce((s, d) => s + d.__value, 0) }]
      : head;
    const total = slices.reduce((s, d) => s + d.__value, 0) || 1;
    const colour = (i: number) => (i < DONUT_SLOTS ? series[i] : (dark ? '#5b655c' : '#8b968c'));

    return (
      <div className="flex flex-col items-center gap-4 sm:flex-row">
        <ResponsiveContainer width="100%" height={height} className="max-w-[260px]">
          <PieChart>
            <Pie data={slices} dataKey="__value" nameKey="__label" innerRadius="58%" outerRadius="88%" paddingAngle={2} stroke="var(--gb-surface)" strokeWidth={2}>
              {slices.map((_, i) => <Cell key={i} fill={colour(i)} />)}
            </Pie>
            <RTooltip content={<ChartTooltip valueType={yType} nameLabel={yLabel} />} />
          </PieChart>
        </ResponsiveContainer>
        <ul className="min-w-0 flex-1 space-y-1.5">
          {slices.map((d, i) => (
            <li key={d.__label} className="flex items-center gap-2 text-sm">
              <span className="size-2.5 shrink-0 rounded-sm" style={{ background: colour(i) }} aria-hidden />
              <span className="min-w-0 flex-1 truncate text-muted">{d.__label.replace(/_/g, ' ')}</span>
              <span className="tabular-nums text-content">{yType === 'money' ? inr(d.__value) : num(d.__value)}</span>
              <span className="w-11 text-right tabular-nums text-subtle">{((d.__value / total) * 100).toFixed(0)}%</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Bar — horizontal, so a 40-character retailer name is readable instead of rotated to 45°.
  const top = [...ordered].sort((a, b) => b.__value - a.__value).slice(0, 12);
  /*
   * The plot is sized to the DATA, not to a fixed panel height. A fixed 260px split between three
   * bars left each one marooned in white space with the axis miles away — the chart read as a bug.
   * One band per row keeps three bars compact and twelve bars legible, and the clamp stops either
   * end running away.
   */
  const barH = Math.min(460, Math.max(150, top.length * 34 + 44));
  const paint = chart.colorBy
    ? groupColours(rows.map((r) => r[chart.colorBy!]), series, dark)
    : null;
  const barColour = (i: number) => (paint ? paint.of(top[i].__group) : series[0]);

  return (
    <>
    {/* A legend whenever more than one group is on screen: identity is never colour alone. */}
    {paint && paint.multi && (
      <ul className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {paint.legend.map((l) => (
          <li key={l.label} className="flex items-center gap-1.5 type-caption text-muted">
            <span className="size-2.5 shrink-0 rounded-sm" style={{ background: l.colour }} aria-hidden />
            {String(l.label).replace(/_/g, ' ')}
          </li>
        ))}
      </ul>
    )}
    <ResponsiveContainer width="100%" height={barH}>
      <BarChart data={top} layout="vertical" margin={{ top: 4, right: 52, left: 4, bottom: 4 }} barCategoryGap="22%">
        <CartesianGrid horizontal={false} stroke={grid} />
        <XAxis type="number" tickFormatter={compact} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: axisInk }} />
        <YAxis
          type="category" dataKey="__label" width={190} tickLine={false} axisLine={false}
          interval={0}
          tick={{ fontSize: 11, fill: axisInk }}
          // Cut to one line here rather than letting recharts wrap: a wrapped category label pushes
          // its own band out of alignment with the bar it names. Trimmed from the FRONT, because a
          // code's distinguishing characters are at its end ("PHASEA-17-A" vs "PHASEA-17-B").
          tickFormatter={(v: string) => (v.length > 26 ? `…${v.slice(v.length - 25)}` : v)}
        />
        <RTooltip cursor={{ fill: dark ? '#ffffff0d' : '#00000008' }} content={<ChartTooltip valueType={yType} nameLabel={yLabel} />} />
        {/* fillOpacity, not a lighter hue: the bars sit back so the figures and labels read first,
            while the colour keeps the exact identity the legend names. A saturated fill across a
            600px bar is the loudest thing on the page, which is the wrong thing to be loudest. */}
        <Bar dataKey="__value" fill={series[0]} fillOpacity={0.82} radius={[0, 4, 4, 0]} maxBarSize={18}>
          {top.map((_, i) => <Cell key={i} fill={barColour(i)} />)}
          <LabelList dataKey="__value" position="right" formatter={compact} style={{ fontSize: 11, fill: axisInk }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Option badges
 *
 * A dropdown of sixty districts, or of every staff member, is a wall of identical lines until the one
 * attribute that separates them is coloured. Which authority does the colouring depends on what the
 * badge IS — so a role goes to `roleTone()` and a status to `statusTone()` (through StatusBadge),
 * the same functions that colour those codes on every other screen. Only the two vocabularies the app
 * has no map for — a territory level and a severity — are defined here.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The pictograms an option may ask for, by name.
 *
 * A short allow-list rather than "any lucide icon": the catalog names a CONCEPT ("cash", "bank") and
 * this file decides what it looks like, so the icon for cash is the same wherever cash appears. An
 * unrecognised name renders nothing, which means a new option can never break a dropdown.
 */
const OPTION_ICONS: Record<string, typeof Banknote> = {
  cash: Banknote, upi: Smartphone, bank: Landmark, cheque: ScrollText, card: CreditCard,
  other: CircleDashed, monitor: Monitor, mobile: Smartphone, store: Store,
  onBehalf: UserCog, approved: CalendarCheck, auto: Bot,
};

/**
 * Tones for the small vocabularies that are not statuses, roles or severities but still mean
 * something — how money arrived, which way an order came in. Grey for all of them threw away the one
 * grouping a collections reader is actually asking about.
 */
const WORD_TONE: Record<string, TagTone> = {
  cash: 'green', digital: 'blue', bank: 'purple', instrument: 'gold', other: 'neutral',
  office: 'blue', 'field staff': 'green', 'self-serve': 'gold',
  'in the app': 'green', 'approved later': 'blue', system: 'neutral',
};

/** zone → state → district → beat, coarsest to finest, so the depth is legible as a colour. */
const LEVEL_TONE: Record<string, TagTone> = {
  zone: 'purple', state: 'blue', district: 'green', beat: 'gold',
};

/** Shared by complaint severity, expiry urgency and the aging buckets — all "how bad is it" scales. */
const SEVERITY_TONE: Record<string, TagTone> = {
  CRIT: 'red', CRITICAL: 'red', HIGH: 'gold', MED: 'blue', MEDIUM: 'blue', LOW: 'neutral',
  MASTER: 'gold', PRIMARY: 'gold', WATCH: 'gold', OVERDUE: 'red', CURRENT: 'green',
};

export function OptionBadge({ badge, kind }: { badge: string; kind?: ReportOptionBadgeKind }) {
  const text = badge.replace(/_/g, ' ');
  if (kind === 'status') return <StatusBadge status={badge} dot={false} />;
  const tone: TagTone =
    kind === 'role' ? roleTone(badge)
      : kind === 'level' ? (LEVEL_TONE[badge.toLowerCase()] ?? 'neutral')
        : kind === 'severity' ? (SEVERITY_TONE[badge.toUpperCase()] ?? 'neutral')
          : (WORD_TONE[badge.toLowerCase()] ?? 'neutral');
  return <Tag tone={tone} style={{ margin: 0 }}>{text}</Tag>;
}

/** One dropdown row: the pack shot, the name, the quiet disambiguator, and the coloured attribute. */
function OptionRow({ o, kind }: { o: ReportFilterOption; kind?: ReportOptionBadgeKind }) {
  const badge = o.badge ?? (kind && kind !== 'plain' ? String(o.value) : undefined);
  const badgeKind = o.badgeKind ?? kind;
  const Icon = o.icon ? OPTION_ICONS[o.icon] : undefined;
  return (
    <span className="flex w-full min-w-0 items-center gap-2">
      {Icon && <Icon className="size-4 shrink-0 text-muted" aria-hidden />}
      {o.image && (
        // `onError` hides a dead link rather than leaving a broken-image glyph in the row — a product
        // whose photo has been removed should read as a product with no photo, not as a broken row.
        <img
          src={fileUrl(o.image)}
          alt=""
          loading="lazy"
          className="size-7 shrink-0 rounded-md border border-line bg-surface-2 object-contain"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <span className="min-w-0 flex-1 truncate">{o.label}</span>
      {o.hint && <span className="shrink-0 type-caption text-subtle">{o.hint}</span>}
      {badge && <OptionBadge badge={badge} kind={badgeKind} />}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * One filter control
 *
 * Rendered from the catalog's declaration, so a filter added to a report on the server appears on the
 * screen with no frontend change — and every report's filters look and behave the same.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

export function FilterField({ filter, value, onChange, values, compact }: {
  filter: ReportFilterDef;
  value: any;
  onChange: (v: any) => void;
  /** Every filter's current value, so a dependent filter can narrow to the parent that is set. */
  values?: Record<string, any>;
  /**
   * The parameter-bar form: a 10px label over a 32px control, and the hint moved into a tooltip.
   *
   * Same control, same options, same value — only the chrome shrinks. One implementation rather than
   * a second "compact filter" component, because two would be two things to keep in agreement.
   */
  compact?: boolean;
}) {
  const size = compact ? 'small' : undefined;
  const control = (): ReactNode => {
    if (filter.type === 'number') {
      return (
        <InputNumber
          className="w-full"
          size={size}
          value={value === undefined || value === '' ? null : Number(value)}
          onChange={(v: any) => onChange(v === null || v === undefined || v === '' ? undefined : v)}
          placeholder={filter.placeholder ?? 'Any'}
        />
      );
    }
    if (filter.type === 'text') {
      return (
        <Input
          allowClear
          size={size}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          placeholder={filter.placeholder ?? 'Any'}
        />
      );
    }
    const multiple = filter.type === 'multiselect';
    /*
     * `label` stays a plain STRING so the closed control and the search box both work on text; the
     * decorated row goes in through `optionRender`, and the chosen value renders from `chip`. Putting
     * the badge into `label` instead would make the option a ReactNode, which the search cannot read
     * and the selected-value chip renders as "[object Object]". `search` folds the hint and badge in
     * so typing "Pune" or "state" both find the territory.
     */
    /*
     * A dependent filter shows only the options that can still return a row. Picking product group
     * "AGN" and being offered every AminOPF SKU is not a longer list but a wrong one: the two
     * predicates are ANDed in the SQL, so those options are guaranteed to match nothing.
     */
    const narrowed = hasParentValue(filter, values);
    const visible = visibleOptions(filter, values);

    const options = visible.map((o) => ({
      ...o,
      search: [o.label, o.hint, o.badge, o.value].filter(Boolean).join(' '),
      /*
       * The CHOSEN value, as it reads inside the closed control.
       *
       * On the parameter bar that control is 190px wide, and a badge beside the name ("KYC PENDING")
       * took enough of it that the name itself truncated to "S…" — the badge is a useful attribute
       * while CHOOSING and noise once chosen, so the compact form keeps the name and drops it. The
       * dropdown rows are unchanged either way.
       */
      chip: (
        <span className="flex min-w-0 items-center gap-1.5">
          {o.icon && OPTION_ICONS[o.icon]
            ? (() => { const I = OPTION_ICONS[o.icon!]; return <I className="size-3.5 shrink-0 text-muted" aria-hidden />; })()
            : null}
          <span className="truncate">{o.label}</span>
          {!compact && (o.badge ?? (filter.optionKind && filter.optionKind !== 'plain' ? String(o.value) : undefined)) && (
            <OptionBadge
              badge={o.badge ?? String(o.value)}
              kind={o.badgeKind ?? filter.optionKind}
            />
          )}
        </span>
      ),
    }));
    return (
      <Select
        className="w-full"
        size={size}
        allowClear
        showSearch
        optionFilterProp="search"
        optionLabelProp="chip"
        optionRender={(o: any) => <OptionRow o={o} kind={filter.optionKind} />}
        maxTagCount="responsive"
        mode={multiple ? 'multiple' : undefined}
        placeholder={multiple ? 'All' : 'Any'}
        notFoundContent={narrowed ? 'Nothing in this list matches what you picked above.' : undefined}
        value={multiple ? (Array.isArray(value) ? value : value ? [value] : []) : (value ?? undefined)}
        onChange={(v: any) => onChange(Array.isArray(v) ? (v.length ? v : undefined) : (v ?? undefined))}
        options={options}
      />
    );
  };

  if (compact) {
    return (
      <label className="flex min-w-0 flex-col gap-0.5" title={filter.hint}>
        <span className="truncate type-label text-subtle">{filter.label}</span>
        {control()}
      </label>
    );
  }

  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="type-label-strong text-muted">{filter.label}</span>
      {control()}
      {filter.hint && <span className="type-caption text-subtle">{filter.hint}</span>}
    </label>
  );
}
